import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const WINDOW_MS = { '1h': 3600e3, '24h': 86400e3, '7d': 7 * 86400e3, '30d': 30 * 86400e3 };

const tickStyle = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };

// series: { [sensorId]: [[ts_ms, temperature, humidity], ...] }
export function SensorChart({ series, sensors, colors, metric, period, until }) {
    const { t, i18n } = useTranslation();
    const idx = metric === 'humidity' ? 2 : 1;
    const unit = metric === 'humidity' ? ' %' : '°C';

    const rows = useMemo(() => {
        const byTs = new Map();
        for (const [id, pts] of Object.entries(series)) {
            for (const p of pts) {
                if (p[idx] === null || p[idx] === undefined) continue;
                const row = byTs.get(p[0]) ?? { ts: p[0] };
                row[id] = p[idx];
                byTs.set(p[0], row);
            }
        }
        return [...byTs.values()].sort((a, b) => a.ts - b.ts);
    }, [series, idx]);

    const to = until;
    const from = to - WINDOW_MS[period];
    const fmt = (ts) => {
        const d = new Date(ts);
        return period === '7d' || period === '30d'
            ? d.toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit' })
            : d.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
    };

    if (rows.length === 0) {
        return (
            <div className="h-64 flex items-center justify-center text-center text-sm text-muted-foreground px-6">
                {t('sensors.noPoints')}
            </div>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={320}>
            <LineChart data={rows} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.4} />
                <XAxis dataKey="ts" type="number" scale="time" domain={[from, to]} allowDataOverflow
                    tick={tickStyle} tickLine={false} tickFormatter={fmt} />
                <YAxis domain={['auto', 'auto']} tick={tickStyle} tickLine={false} tickFormatter={v => `${v}`} unit={unit} width={52} />
                <Tooltip
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.5rem', fontSize: 12 }}
                    labelFormatter={ts => new Date(ts).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' })}
                    formatter={(v, name) => [`${v}${unit}`, name]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {sensors.map(s => (
                    <Line key={s.id} dataKey={String(s.id)} name={s.name} type="monotone" stroke={colors[s.id]}
                        strokeWidth={2} dot={false} activeDot={{ r: 3 }} connectNulls isAnimationActive={false} />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}
